// src/pages/Todo/TodoPlanner.tsx â€” Daily Planner main page
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

import {
  DndContext,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
} from "@dnd-kit/core";
import { createPortal } from "react-dom";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Sun, CalendarRange, AlertTriangle, History, Search, Filter, Sparkles, RefreshCcw,
  Users, Check, Clock, Flag, User, Briefcase, Pin, Trash2, Pencil,
  PlayCircle, MoreVertical, BarChart3, ChevronDown, ChevronLeft, ChevronRight,
  TrendingUp, Zap, Award, ListTodo, CheckCircle2, Eye, Send, X,
  Building2, Truck, FolderKanban
} from "lucide-react";
import { format, addDays, parseISO, isPast, isToday } from "date-fns";

import { useSelector } from "react-redux";
import { selectNotifications } from "../../redux/slices/notifications";

import todoApi, {
  type PlannerItem,
  type PersonalTodo,
  type AssignedItem,
  type TodoQuickAdd,
  type TodoSummary,
  type TodoUpdate
} from "../../api/todo";
import { tasksApi } from "../../api/tasks";
import { clientsApi } from "../../api/clients";
import { vendorsApi } from "../../api/vendors";
import { projectsApi } from "../../api/projects";

import QuickAdd from "./QuickAdd";

type Bucket = "today" | "upcoming" | "missed";

// ========== Stats Dropdown Component ==========
const StatsDropdown: React.FC<{ summary: TodoSummary; isOpen: boolean; onClose: () => void }> = ({ summary, isOpen, onClose }) => {
  const s = summary;
  const rate = Math.min(100, Math.max(0, s.completion_rate));
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      ref={dropdownRef}
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      className="absolute top-full right-0 mt-2 w-[420px] z-50"
    >
      <div className="gt-dropdown gt-pop-in">
        {/* Header */}
        <div className="gt-cal-head" style={{ paddingTop: 14, paddingBottom: 14 }}>
          <span className="gt-page-sub gt-numerals">
            {format(new Date(), "MMM d, yyyy")}
          </span>
        </div>

        {/* Stats Grid */}
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="gt-stat">
              <div className="gt-stat-label"><ListTodo className="w-3.5 h-3.5" /> Today</div>
              <p className="gt-stat-value">{s.total}</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><CheckCircle2 className="w-3.5 h-3.5" /> Completed</div>
              <p className="gt-stat-value">{s.completed}</p>
              <p className="gt-stat-foot"><em className="gt-serif-italic">{rate}%</em> done</p>
            </div>
            <div className="gt-stat">
              <div className="gt-stat-label"><Clock className="w-3.5 h-3.5" /> Pending</div>
              <p className="gt-stat-value">{s.pending}</p>
            </div>
            <div className="gt-stat" style={{ borderColor: s.overdue > 0 ? 'var(--gt-overlay-red-18)' : undefined }}>
              <div className="gt-stat-label"><AlertTriangle className="w-3.5 h-3.5" /> Overdue</div>
              <p className="gt-stat-value" style={{ color: s.overdue > 0 ? 'var(--gt-red-800)' : undefined }}>{s.overdue}</p>
              {s.overdue > 0 && <p className="gt-stat-foot" style={{ color: 'var(--gt-red-800)' }}>needs attention</p>}
            </div>
          </div>

          {/* Progress Section */}
          <div style={{ borderTop: '1px dashed var(--gt-border-strong)', paddingTop: 16 }}>
            <div className="flex items-center justify-between mb-3">
              <span className="gt-display gt-numerals" style={{ fontSize: 18 }}>{rate}<span style={{ color: 'var(--gt-text-tertiary)', fontSize: 12 }}>%</span></span>
            </div>

            {/* Pie + Bar */}
            <div className="flex items-center justify-between gap-4">
              <div className="relative w-20 h-20">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="var(--gt-surface-2)" strokeWidth="8" />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="var(--gt-primary)"
                    strokeWidth="8"
                    strokeDasharray={2 * Math.PI * 40}
                    strokeDashoffset={2 * Math.PI * 40 * (1 - rate / 100)}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.4,0,0.2,1)' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="gt-display gt-numerals" style={{ fontSize: 16, color: 'var(--gt-text-primary)' }}>{s.total}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="gt-progress-track">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${rate}%` }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="gt-progress-fill"
                  />
                </div>
                <div className="flex justify-between mt-2 gt-page-sub gt-numerals" style={{ fontSize: 11 }}>
                  <span>{s.completed} done</span>
                  <span>{s.pending} left</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="gt-rule" />
          <div className="flex justify-between gt-page-sub gt-numerals" style={{ fontSize: 11 }}>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              <span>Completion <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>{rate}%</em></span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              <span>{rate >= 70 ? "Excellent" : rate >= 40 ? "Good" : "Keep going"}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// idâ†’name maps for the optional client / vendor / project tags, resolved
// client-side from the lists the planner loads (the API returns ids only).
type EntityNameMaps = {
  client: Record<string, string>;
  vendor: Record<string, string>;
  project: Record<string, string>;
};

// ========== Todo Card Component ==========
const TodoCardComponent: React.FC<{
  item: PlannerItem;
  onToggle?: (it: PersonalTodo) => void;
  onStatusChange?: (it: PersonalTodo, newStatus: string) => void;
  onOpen?: (it: PlannerItem) => void;
  onLink?: (it: AssignedItem) => void;
  onUnlink?: (it: AssignedItem) => void;
  onDelete?: (it: PersonalTodo) => void;
  onEdit?: (it: PersonalTodo, changes: TodoUpdate) => void;
  variant?: "today" | "upcoming" | "missed";
  // Linkage between checklist Todos and their source task
  sourceTaskStatus?: string | null;
  onStartFromChecklist?: (it: PersonalTodo, sourceTaskId: string) => void;
  onPreparorComplete?: (it: PersonalTodo, sourceTaskId: string) => void;
  // Reviewer-side actions on assigned tasks
  onReviewerView?: (it: AssignedItem) => void;
  onReviewerComplete?: (it: AssignedItem) => void;
  // Preparor-side actions on assigned tasks (TODO -> Start, IN_PROGRESS -> Complete)
  onAssignedStart?: (it: AssignedItem) => void;
  onAssignedComplete?: (it: AssignedItem) => void;
  // idâ†’name maps for the client/vendor/project tag chips.
  entityNames?: EntityNameMaps;
}> = ({
  item, onToggle, onStatusChange, onOpen, onLink, onUnlink, onDelete, onEdit, variant = "today",
  sourceTaskStatus, onStartFromChecklist, onPreparorComplete,
  onReviewerView, onReviewerComplete,
  onAssignedStart, onAssignedComplete, entityNames,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editPriority, setEditPriority] = useState(item.priority);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);
  const PRIO_COLORS: Record<string, string> = {
    URGENT: 'var(--gt-red-600)', HIGH: 'var(--gt-orange-600)', MEDIUM: 'var(--gt-amber-600)', LOW: 'var(--gt-blue-600)',
  };
  const isPersonal = item.source === "personal";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: item.source !== "personal",
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    minHeight: 0,
    opacity: isDragging ? 0.25 : 1,
  };
  const isDone = isPersonal ? item.status === "COMPLETED" : item.status === "DONE";
  const isInProgress = isPersonal && item.status === "IN_PROGRESS";
  const dueDate = item.due_date ? parseISO(item.due_date) : null;
  const isOverdue = dueDate && !isDone && isPast(dueDate) && !isToday(dueDate);

  const priorityBarClass = {
    URGENT: "is-urgent",
    HIGH: "is-high",
    MEDIUM: "is-medium",
    LOW: "is-low",
  } as const;

  const priorityPillClass = {
    URGENT: "gt-pill is-urgent",
    HIGH: "gt-pill is-high",
    MEDIUM: "gt-pill is-medium",
    LOW: "gt-pill is-low",
  } as const;

  const statusPillClass: Record<string, string> = {
    PENDING: "gt-pill is-pending",
    IN_PROGRESS: "gt-pill is-progress",
    COMPLETED: "gt-pill is-done",
    DONE: "gt-pill is-done",
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();

    if (!isPersonal || !onStatusChange) return;

    if (item.status === "COMPLETED") {
      // uncheck â†’ back to pending
      onStatusChange(item, "PENDING");
    } else {
      // first click â†’ complete
      onStatusChange(item, "COMPLETED");
    }
  };


  const handleInProgress = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPersonal && onStatusChange && item.status !== "IN_PROGRESS") {
      onStatusChange(item, "IN_PROGRESS");
    }
  };

  const handleEditSave = async () => {
    if (isPersonal && onEdit) {
      await onEdit(item, { title: editTitle, priority: editPriority as any });
      setIsEditing(false);
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...(isPersonal ? attributes : {})} {...(isPersonal ? listeners : {})}>
    <div
      className={`group gt-task-card overflow-visible is-prio-${(item.priority || 'medium').toLowerCase()} ${isDone ? "is-done" : ""} ${isOverdue ? "is-overdue" : ""}`}
      style={{ minHeight: 0, cursor: isPersonal ? 'grab' : 'pointer', touchAction: isPersonal ? 'none' : 'auto' }}
      onClick={() => onOpen?.(item)}
    >
      <div className={`gt-prio-bar ${priorityBarClass[item.priority as keyof typeof priorityBarClass]}`} />

      <div className="p-3 pl-4 pr-10">
        {/* Compact title row â€” drag indicator + checkbox + title */}
        <div className="flex items-center gap-2 mb-1.5">
          {isPersonal && (
            <div
              className="shrink-0 p-1 -m-1"
              style={{ color: 'var(--gt-text-tertiary)', pointerEvents: 'none' }}
              aria-hidden
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" />
              </svg>
            </div>
          )}
          {isPersonal ? (
            <button
              onClick={handleCheckboxClick}
              className="shrink-0 flex items-center justify-center"
              style={{
                width: 18, height: 18,
                borderRadius: 999,
                border: `2px solid ${isDone ? 'var(--gt-accent-strong)' : isInProgress ? 'var(--gt-blue-500)' : 'var(--gt-border-strong)'}`,
                background: isDone ? 'var(--gt-accent-strong)' : isInProgress ? 'var(--gt-blue-100)' : 'transparent',
                color: isDone ? 'var(--gt-white)' : isInProgress ? 'var(--gt-blue-800)' : 'var(--gt-primary)',
                transition: 'all 200ms cubic-bezier(0.33, 1, 0.68, 1)',
              }}
            >
              {isDone && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
              {isInProgress && !isDone && <PlayCircle className="w-2.5 h-2.5" />}
            </button>
          ) : (
            <div className="shrink-0 flex items-center justify-center"
              style={{ width: 18, height: 18, borderRadius: 999, background: 'var(--gt-surface-2)', color: 'var(--gt-text-tertiary)' }}>
              <Briefcase className="w-2.5 h-2.5" />
            </div>
          )}
          {!isEditing && (
            <h3 className="gt-display flex-1 min-w-0 line-clamp-1 group-hover:line-clamp-none transition-all duration-200" style={{
              fontSize: 14, fontWeight: 600,
              color: isDone ? 'var(--gt-text-disabled)' : 'var(--gt-text-primary)',
              textDecoration: isDone ? 'line-through' : 'none',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}>
              {item.title}
            </h3>
          )}
        </div>

        {/* Full-width content area */}
        <div className="flex">
          <div className="flex-1 min-w-0 space-y-1">
            {isEditing ? (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <input
                  autoFocus
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="gt-input"
                />
                <Select
                  value={editPriority}
                  onValueChange={(v) => setEditPriority(v as any)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <button onClick={handleEditSave} className="gt-btn is-primary is-sm">Save</button>
                  <button onClick={() => setIsEditing(false)} className="gt-btn is-ghost is-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                {/* Description / notes â€” kept visible in BOTH states */}
                {item.description && (
                  <p className="line-clamp-2 group-hover:line-clamp-none transition-all duration-200" style={{
                    fontSize: 12,
                    color: isDone ? 'var(--gt-text-disabled)' : 'var(--gt-text-tertiary)',
                  }}>
                    {item.description}
                  </p>
                )}

                {isDone ? (
                  <div className="mt-2.5" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--gt-green-600)', fontWeight: 700 }}>
                    <Check className="w-3.5 h-3.5" /> Completed
                  </div>
                ) : (
                  <>
                    {/* Inline metadata row â€” informational text, not buttons */}
                    <div style={{
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                      gap: '6px 10px', marginTop: 8, fontSize: 11.5,
                      color: 'var(--gt-text-tertiary)', fontWeight: 500,
                    }}>
                      {isPersonal && (item as any).source_task_id && (
                        <>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--gt-orange-800)', fontWeight: 600 }} title="Created from a task checklist">
                            <Briefcase className="w-3 h-3" /> From Task
                          </span>
                          <span style={{ color: 'var(--gt-border-strong)' }}>Â·</span>
                        </>
                      )}
                      {(() => {
                        // A personal Todo created by /todo/assign carries a
                        // created_by_name. Show it as "Assigned Â· by X" so
                        // the recipient knows where it came from. Self-
                        // created personal Todos stay labelled "Personal".
                        const personalAssignedFrom = isPersonal
                          ? (item as PersonalTodo).created_by_name || null
                          : null;
                        const isAssignedLooking = !isPersonal || !!personalAssignedFrom;
                        const creator = !isPersonal
                          ? (item as AssignedItem).created_by_name
                          : personalAssignedFrom;
                        return (
                          <>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {isAssignedLooking ? <Briefcase className="w-3 h-3" /> : <User className="w-3 h-3" />}
                              {isAssignedLooking ? 'Assigned' : 'Personal'}
                            </span>
                            {creator && (
                              <>
                                <span style={{ color: 'var(--gt-border-strong)' }}>Â·</span>
                                <span
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                  title="Assigned by"
                                >
                                  <User className="w-3 h-3" />
                                  by {creator}
                                </span>
                              </>
                            )}
                          </>
                        );
                      })()}
                      <span style={{ color: 'var(--gt-border-strong)' }}>Â·</span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        color: PRIO_COLORS[item.priority] || PRIO_COLORS.MEDIUM,
                        fontWeight: 700, letterSpacing: '0.02em',
                      }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
                        {item.priority}
                      </span>
                      <span style={{ color: 'var(--gt-border-strong)' }}>Â·</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {item.status.replace(/_/g, " ").toLowerCase()}
                      </span>
                      {dueDate && (
                        <>
                          <span style={{ color: 'var(--gt-border-strong)' }}>Â·</span>
                          <span className="gt-numerals" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            color: isOverdue ? 'var(--gt-red-700)' : 'var(--gt-text-tertiary)',
                          }}>
                            <Clock className="w-3 h-3" />
                            {format(dueDate, "MMM d")}
                          </span>
                        </>
                      )}
                      {isPersonal && (() => {
                        const t = item as PersonalTodo;
                        const chips = [
                          t.project_id && { Icon: FolderKanban, label: entityNames?.project[t.project_id] || 'Project' },
                          t.client_id && { Icon: Building2, label: entityNames?.client[t.client_id] || 'Client' },
                          t.vendor_id && { Icon: Truck, label: entityNames?.vendor[t.vendor_id] || 'Vendor' },
                        ].filter(Boolean) as { Icon: any; label: string }[];
                        return chips.map((c, i) => (
                          <React.Fragment key={`ent-${i}`}>
                            <span style={{ color: 'var(--gt-border-strong)' }}>Â·</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title={c.label}>
                              <c.Icon className="w-3 h-3" /> {c.label}
                            </span>
                          </React.Fragment>
                        ));
                      })()}
                    </div>

                    {/* Action Buttons */}
                    {isPersonal && (() => {
                      const sourceTaskId = (item as PersonalTodo & { source_task_id?: string | null }).source_task_id || null;
                      const fromChecklist = !!sourceTaskId;
                      const todoStatus = (item as PersonalTodo).status;
                      const thisItemStarted = todoStatus === "IN_PROGRESS";
                      if (fromChecklist) {
                        // This specific item hasn't been started yet â†’ show "Start".
                        if (!thisItemStarted) {
                          return (
                            <div className="grid grid-cols-1 gap-2 mt-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onStartFromChecklist?.(item as PersonalTodo, sourceTaskId!);
                                }}
                                className="gt-btn"
                                style={{
                                  justifyContent: 'center', width: '100%', padding: '9px 14px',
                                  background: 'var(--gt-blue-600)', color: 'var(--gt-white)',
                                  border: '1px solid transparent', fontWeight: 700,
                                  boxShadow: '0 1px 2px var(--gt-overlay-blue-25)',
                                }}
                              >
                                <PlayCircle className="w-4 h-4" /> Start
                              </button>
                            </div>
                          );
                        }

                        // Source task is in flight (IN_PROGRESS/IN_REVIEW/BLOCKED) â†’ only Complete.
                        return (
                          <div className="grid grid-cols-1 gap-2 mt-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPreparorComplete?.(item as PersonalTodo, sourceTaskId!);
                              }}
                              className="gt-btn"
                              style={{
                                justifyContent: 'center', width: '100%', padding: '9px 14px',
                                background: 'var(--gt-green-600)', color: 'var(--gt-white)',
                                border: '1px solid transparent', fontWeight: 700,
                                boxShadow: '0 1px 2px var(--gt-overlay-green-25)',
                              }}
                            >
                              <Check className="w-4 h-4" /> Complete
                            </button>
                          </div>
                        );
                      }

                      // Plain personal todo â€” keep the original In Progress + Complete pair.
                      return (
                        <div className={`grid ${isInProgress ? 'grid-cols-1' : 'grid-cols-2'} gap-2 mt-3`}>
                          {!isInProgress && onStatusChange && (
                            <button
                              onClick={handleInProgress}
                              className="gt-btn"
                              style={{
                                justifyContent: 'center', width: '100%', padding: '9px 14px',
                                background: 'var(--gt-blue-100)', color: 'var(--gt-blue-800)',
                                border: '1px solid transparent', fontWeight: 600,
                              }}
                            >
                              <PlayCircle className="w-4 h-4" /> In Progress
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onStatusChange?.(item, "COMPLETED");
                            }}
                            className="gt-btn"
                            style={{
                              justifyContent: 'center', width: '100%', padding: '9px 14px',
                              background: 'var(--gt-green-600)', color: 'var(--gt-white)', border: '1px solid transparent', fontWeight: 700,
                              boxShadow: '0 1px 2px var(--gt-overlay-green-25)',
                            }}
                          >
                            <Check className="w-4 h-4" /> Complete
                          </button>
                        </div>
                      );
                    })()}

                    {/* Preparor-side actions on assigned tasks: TODO â†’ Start,
                        IN_PROGRESS â†’ Complete. Gated on the caller actually
                        being a PREPAROR so creators/observers don't get the
                        buttons. Mirrors the In Progress / Complete pair on
                        personal Todos so assignees can drive the task from
                        their planner. */}
                    {!isPersonal
                      && ((item as AssignedItem).my_roles || []).includes("PREPAROR")
                      && ((item as AssignedItem).status === "TODO"
                          || (item as AssignedItem).status === "IN_PROGRESS")
                      && (
                      <div className={`grid ${
                        (item as AssignedItem).status === "IN_PROGRESS" ? 'grid-cols-1' : 'grid-cols-2'
                      } gap-2 mt-3`}>
                        {(item as AssignedItem).status === "TODO" && onAssignedStart && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onAssignedStart(item as AssignedItem);
                            }}
                            className="gt-btn"
                            style={{
                              justifyContent: 'center', width: '100%', padding: '9px 14px',
                              background: 'var(--gt-blue-100)', color: 'var(--gt-blue-800)',
                              border: '1px solid transparent', fontWeight: 600,
                            }}
                          >
                            <PlayCircle className="w-4 h-4" /> In Progress
                          </button>
                        )}
                        {onAssignedComplete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onAssignedComplete(item as AssignedItem);
                            }}
                            className="gt-btn"
                            style={{
                              justifyContent: 'center', width: '100%', padding: '9px 14px',
                              background: 'var(--gt-green-600)', color: 'var(--gt-white)',
                              border: '1px solid transparent', fontWeight: 700,
                              boxShadow: '0 1px 2px var(--gt-overlay-green-25)',
                            }}
                          >
                            <Check className="w-4 h-4" /> Complete
                          </button>
                        )}
                      </div>
                    )}

                    {/* Reviewer-side actions: assigned task in IN_REVIEW â†’ View + Complete.
                        Gated on the caller actually being a REVIEWER on this
                        task. Preparors see their task flip to IN_REVIEW too
                        once they send it for review â€” they should NOT get the
                        Reviewer affordances. my_roles is bulk-loaded by the
                        planner endpoints on the backend. */}
                    {!isPersonal
                      && (item as AssignedItem).status === "IN_REVIEW"
                      && ((item as AssignedItem).my_roles || []).includes("REVIEWER")
                      && (
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onReviewerView?.(item as AssignedItem);
                          }}
                          className="gt-btn"
                          style={{
                            justifyContent: 'center', width: '100%', padding: '9px 14px',
                            background: 'var(--gt-purple-100)', color: 'var(--gt-purple-800)',
                            border: '1px solid transparent', fontWeight: 600,
                          }}
                        >
                          <Eye className="w-4 h-4" /> View
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onReviewerComplete?.(item as AssignedItem);
                          }}
                          className="gt-btn"
                          style={{
                            justifyContent: 'center', width: '100%', padding: '9px 14px',
                            background: 'var(--gt-green-600)', color: 'var(--gt-white)',
                            border: '1px solid transparent', fontWeight: 700,
                            boxShadow: '0 1px 2px var(--gt-overlay-green-25)',
                          }}
                        >
                          <Check className="w-4 h-4" /> Complete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Action Icons */}
          <div className="absolute top-2 right-2 flex items-center gap-1">
            {!isPersonal && variant === "today" && (
              item.link_id ? (
                <button onClick={(e) => { e.stopPropagation(); onUnlink?.(item); }} className="gt-icon-btn is-sm opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--gt-dnd)' }}>
                  <Pin className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); onLink?.(item); }} className="gt-icon-btn is-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  <Pin className="w-3.5 h-3.5" />
                </button>
              )
            )}

            {isPersonal && (onEdit || onDelete) && !isEditing && (
              showDeleteConfirm ? (
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => { onDelete?.(item); setShowDeleteConfirm(false); }} className="gt-btn is-danger is-sm">Delete</button>
                  <button onClick={() => setShowDeleteConfirm(false)} className="gt-btn is-ghost is-sm">Cancel</button>
                </div>
              ) : (
                <div ref={menuRef} style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
                    className="gt-icon-btn is-sm"
                    aria-label="More actions"
                    title="More actions"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {menuOpen && (
                    <div
                      className="gt-dropdown"
                      style={{
                        position: 'absolute', right: 0, top: '110%',
                        minWidth: 140, padding: 6, zIndex: 50,
                      }}
                    >
                      {onEdit && (
                        <button
                          onClick={() => { setMenuOpen(false); setIsEditing(true); }}
                          className="gt-dropdown-item"
                          style={{ borderRadius: 8 }}
                        >
                          <Pencil className="w-4 h-4" /> Edit
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }}
                          className="gt-dropdown-item"
                          style={{ color: 'var(--gt-dnd)', borderRadius: 8 }}
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

// ========== Main TodoPlanner Component ==========
export default function TodoPlanner() {
  const navigate = useNavigate();
  const [todayItems, setTodayItems] = useState<PlannerItem[]>([]);
  const [upcomingItems, setUpcomingItems] = useState<PlannerItem[]>([]);
  const [missedItems, setMissedItems] = useState<PlannerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<TodoSummary | null>(null);
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [activeDrag, setActiveDrag] = useState<PlannerItem | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [startedTaskIds, setStartedTaskIds] = useState<Set<string>>(new Set());
  // Client / vendor / project lists, loaded once to resolve planner-card tag names.
  const [entClients, setEntClients] = useState<{ id: string; clientName: string }[]>([]);
  const [entVendors, setEntVendors] = useState<{ id: string; clientName: string }[]>([]);
  const [entProjects, setEntProjects] = useState<{ id: string; name: string }[]>([]);

  // Mobile-only carousel state for Today / Upcoming / Missed columns.
  // On desktop the carousel just renders as a 3-col grid and these are no-ops.
  const COLUMN_LABELS = ['Today', 'Upcoming', 'Missed'] as const;
  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeColumnIdx, setActiveColumnIdx] = useState(0);

  const slideToColumn = useCallback((idx: number) => {
    const track = carouselRef.current;
    if (!track) return;
    const target = Math.max(0, Math.min(2, idx));
    // Each slide is the full track width on mobile. scrollBy is more robust
    // than scrollTo for snap containers when child widths can be 0 briefly.
    track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
    setActiveColumnIdx(target);
  }, []);

  const onCarouselScroll = useCallback(() => {
    const track = carouselRef.current;
    if (!track || track.clientWidth === 0) return;
    const idx = Math.round(track.scrollLeft / track.clientWidth);
    setActiveColumnIdx((prev) => (prev === idx ? prev : idx));
  }, []);

  // Mobile-only filters popover â€” collapses the Priority + Status dropdowns
  // into a single Filter icon so the bar stays compact on phones.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { user } = useAuth();
  const userRole = user?.role;

  // Notification toasts are now driven app-wide from Layout (useReminderStream),
  // so the planner no longer mounts its own stream.

  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, upcomingRes, missedRes, summaryRes] = await Promise.all([
        todoApi.today(),
        todoApi.upcoming(30),
        todoApi.missed(),
        todoApi.summary()
      ]);

      const mergeItems = (personal: PersonalTodo[], assigned: AssignedItem[]) => [...personal, ...assigned];
      setTodayItems(mergeItems(todayRes.data.personal || [], todayRes.data.assigned || []));
      setUpcomingItems(mergeItems(upcomingRes.data.personal || [], upcomingRes.data.assigned || []));
      setMissedItems(mergeItems(missedRes.data.personal || [], missedRes.data.assigned || []));
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Auto-refresh the planner when a new ASSIGNED todo notification arrives via
  // WebSocket so the assignee sees the card without manually reloading.
  const notifications = useSelector(selectNotifications);
  const lastAssignedIdRef = useRef<string | null>(null);
  useEffect(() => {
    const latest = notifications.find((n: any) => n.kind === "ASSIGNED");
    if (latest && latest.id !== lastAssignedIdRef.current) {
      lastAssignedIdRef.current = latest.id;
      refreshData();
    }
  }, [notifications, refreshData]);

  // Lazy-load client / vendor / project lists once so planner cards can show
  // the linked entity's name. Best-effort â€” failures leave the maps empty and
  // cards simply omit the chip.
  useEffect(() => {
    clientsApi.list({ page_size: 200 }).then((r) => setEntClients(r.data?.items || [])).catch(() => {});
    vendorsApi.list({ page_size: 200 }).then((r) => setEntVendors(r.data?.items || [])).catch(() => {});
    projectsApi.list({ page_size: 200 }).then((r) => setEntProjects(r.data?.items || [])).catch(() => {});
  }, []);

  const entityNames = useMemo<EntityNameMaps>(() => ({
    client: Object.fromEntries(entClients.map((c) => [c.id, c.clientName])),
    vendor: Object.fromEntries(entVendors.map((v) => [v.id, v.clientName])),
    project: Object.fromEntries(entProjects.map((p) => [p.id, p.name])),
  }), [entClients, entVendors, entProjects]);

  const filterItems = useCallback((items: PlannerItem[]) => {
    return items.filter(item => {
      const matchesSearch = !search || item.title.toLowerCase().includes(search.toLowerCase());
      const matchesPriority = priorityFilter === "ALL" || item.priority === priorityFilter;
      const matchesStatus = statusFilter === "ALL" || item.status === statusFilter;
      return matchesSearch && matchesPriority && matchesStatus;
    });
  }, [search, priorityFilter, statusFilter]);

  const filteredToday = useMemo(() => filterItems(todayItems), [todayItems, filterItems]);
  const filteredUpcoming = useMemo(() => filterItems(upcomingItems), [upcomingItems, filterItems]);
  const filteredMissed = useMemo(() => filterItems(missedItems), [missedItems, filterItems]);

  const handleQuickAdd = async (
    payload: TodoQuickAdd & { assignees?: string[] },
  ) => {
    const { assignees, ...rest } = payload;
    if (assignees && assignees.length > 0) {
      // Push the quick-add onto each recipient's planner as a personal
      // Todo (one row per user_id). The backend stamps Todo.created_by
      // with the caller so the recipient's card renders "Assigned by X".
      // Intentionally NOT creating a Task here â€” the planner composer is
      // for lightweight to-dos; anyone needing the full task lifecycle
      // (reviewers, approval chain, attachments) uses Task Create.
      await todoApi.assign({ ...rest, user_ids: assignees });
    } else {
      await todoApi.quickAdd(rest);
    }
    refreshData();
  };

  const isSourceTaskNotStarted = useCallback(
    (todoItem: PersonalTodo): boolean => {
      const sourceTaskId = (todoItem as any).source_task_id as string | undefined;
      if (!sourceTaskId) return false;
      const allAssigned: AssignedItem[] = [
        ...todayItems,
        ...upcomingItems,
        ...missedItems,
      ].filter(
        (it): it is AssignedItem => (it as any).source === "assigned",
      );
      const sourceTask = allAssigned.find((t) => t.id === sourceTaskId);
      // If the source task isn't visible in any of our buckets we can't
      // confirm its status from the client â€” let the request fly and rely
      // on the backend guard.
      if (!sourceTask) return false;
      return (sourceTask as any).status === "TODO";
    },
    [todayItems, upcomingItems, missedItems],
  );

  const handleTodoMutationError = (
    err: any,
    fallbackMessage = "Couldn't update the task.",
  ): false => {
    const detail =
      err?.response?.data?.detail ??
      err?.response?.data?.message ??
      err?.message;
    const status = err?.response?.status;
    if (status === 409 && typeof detail === "string") {
      // Backend already phrased this in English â€” just show it as-is.
      toast.error(detail);
    } else {
      toast.error(typeof detail === "string" ? detail : fallbackMessage);
    }
    return false;
  };

  const handleToggle = async (item: PersonalTodo) => {
    // Block "complete" via checkbox click when the source task is TODO.
    // Uncompleting (toggling back to PENDING) is always allowed.
    const willComplete = item.status !== "COMPLETED";
    if (willComplete && isSourceTaskNotStarted(item)) {
      toast.error("Start the task first before completing its checklist items.");
      return;
    }
    try {
      await todoApi.toggle(item.id);
      refreshData();
      toast.success(item.status === "COMPLETED" ? "Task reopened" : "Task completed! ðŸŽ‰");
    } catch (err) {
      handleTodoMutationError(err);
    }
  };

  const handleStatusChange = async (item: PersonalTodo, newStatus: string) => {
    // Same guard for the explicit Complete button. Any non-COMPLETED
    // transition (e.g. In Progress) is allowed regardless.
    if (newStatus === "COMPLETED" && isSourceTaskNotStarted(item)) {
      toast.error("Start the task first before completing its checklist items.");
      return;
    }
    try {
      await todoApi.update(item.id, { status: newStatus as any });
      refreshData();
      toast.success(`Status updated to ${newStatus.replace(/_/g, " ")}`);
    } catch (err) {
      handleTodoMutationError(err);
    }
  };
  const getSourceTaskStatus = useCallback((todoItem: PersonalTodo): string | null => {
    const sourceTaskId = (todoItem as any).source_task_id as string | undefined;
    if (!sourceTaskId) return null;
    const allAssigned: AssignedItem[] = [
      ...todayItems, ...upcomingItems, ...missedItems,
    ].filter((it): it is AssignedItem => (it as any).source === "assigned");
    const visible = allAssigned.find(t => t.id === sourceTaskId)?.status ?? null;
    if (visible) return visible;
    // Session override: we know we just started this one even if no bucket sees it.
    if (startedTaskIds.has(sourceTaskId)) return "IN_PROGRESS";
    return null;
  }, [todayItems, upcomingItems, missedItems, startedTaskIds]);

  const countRemainingChecklistSiblings = useCallback((sourceTaskId: string, excludeTodoId: string): number => {
    const allPersonal: PersonalTodo[] = [
      ...todayItems, ...upcomingItems, ...missedItems,
    ].filter((it): it is PersonalTodo => (it as any).source === "personal");
    return allPersonal.filter(t =>
      (t as any).source_task_id === sourceTaskId &&
      t.id !== excludeTodoId &&
      t.status !== "COMPLETED"
    ).length;
  }, [todayItems, upcomingItems, missedItems]);

  // Modal state â€” one for the "send for review" confirm, one for the
  // "you still have other checklist items" block.
  const [reviewModal, setReviewModal] = useState<{
    todo: PersonalTodo;
    sourceTaskId: string;
    taskTitle: string;
  } | null>(null);
  const [remainingModal, setRemainingModal] = useState<{
    taskTitle: string;
    remaining: number;
  } | null>(null);

  const handleStartFromChecklist = async (todoItem: PersonalTodo, sourceTaskId: string) => {
    try {
      await tasksApi.changeStatus(sourceTaskId, { status: "IN_PROGRESS" });
      // Remember the task is now started so the card flips Start â†’ Complete
      // even if /today /upcoming /missed don't surface it on next refresh.
      setStartedTaskIds(prev => {
        if (prev.has(sourceTaskId)) return prev;
        const next = new Set(prev);
        next.add(sourceTaskId);
        return next;
      });
      const todayIso = format(new Date(), "yyyy-MM-dd");
      // Reschedule moves the card between buckets; status flips the
      // checkbox visual. Both are nice-to-haves â€” the task itself starting
      // is the load-bearing write.
      try {
        await todoApi.reschedule(todoItem.id, todayIso);
      } catch {
        /* ignore */
      }
      try {
        await todoApi.update(todoItem.id, { status: "IN_PROGRESS" });
      } catch {
        /* ignore */
      }
      toast.success("Task started ðŸš€");
      refreshData();
    } catch (err) {
      handleTodoMutationError(err, "Couldn't start the task.");
    }
  };

  const handlePreparorComplete = async (todoItem: PersonalTodo, sourceTaskId: string) => {
    const remaining = countRemainingChecklistSiblings(sourceTaskId, todoItem.id);
    const sourceTask = [...todayItems, ...upcomingItems, ...missedItems]
      .find(t => (t as any).source === "assigned" && t.id === sourceTaskId) as AssignedItem | undefined;
    const taskTitle = sourceTask?.title || "this task";

    if (remaining > 0) {
      try {
        await todoApi.update(todoItem.id, { status: "COMPLETED" });
        refreshData();
        setRemainingModal({ taskTitle, remaining });
      } catch (err) {
        handleTodoMutationError(err);
      }
      return;
    }
    // Last remaining item â€” confirm before flipping it COMPLETED + sending
    // the task for review. The confirm handler does both writes.
    setReviewModal({ todo: todoItem, sourceTaskId, taskTitle });
  };

  // Confirm path for the "Send for Review" modal: mark the last todo
  // COMPLETED then submit the task for review.
  const confirmSendForReview = async () => {
    if (!reviewModal) return;
    const { todo: todoItem, sourceTaskId } = reviewModal;
    try {
      // Complete the last checklist todo first â€” backend mirrors this onto
      // the task checklist item and auto-bumps progress to 100%.
      await todoApi.update(todoItem.id, { status: "COMPLETED" });
      // Then formally send for review (or fall back to DONE if no reviewer).
      try {
        await tasksApi.sendForReview(sourceTaskId);
        toast.success("Sent for review ðŸ“¤");
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        if (typeof detail === "string" && /no reviewer/i.test(detail)) {
          // No reviewer configured â€” close the task instead.
          await tasksApi.changeStatus(sourceTaskId, { status: "DONE" });
          toast.success("Task completed âœ“");
        } else {
          throw err;
        }
      }
      setReviewModal(null);
      refreshData();
    } catch (err) {
      handleTodoMutationError(err, "Couldn't send for review.");
    }
  };

  // Reviewer-side actions on assigned tasks shown in the planner.
  const handleReviewerView = (item: AssignedItem) => {
    navigate(`/tasks/${item.id}`);
  };

  const handleReviewerComplete = async (item: AssignedItem) => {
    try {
      await tasksApi.changeStatus(item.id, { status: "DONE" });
      toast.success("Task marked complete âœ“");
      refreshData();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't complete this task.");
    }
  };

  // Preparor-side actions on assigned tasks shown in the planner. Mirrors
  // the In Progress / Complete pair on personal Todos so an assignee can
  // drive the task forward without leaving the planner.
  const handlePreparorAssignedStart = async (item: AssignedItem) => {
    try {
      await tasksApi.changeStatus(item.id, { status: "IN_PROGRESS" });
      toast.success("Task started ðŸš€");
      refreshData();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start this task.");
    }
  };

  const handlePreparorAssignedComplete = async (item: AssignedItem) => {
    // "Complete" on an assigned task = send for review. If the task has no
    // reviewer configured the backend rejects sendForReview; in that case
    // we fall back to marking DONE so the action still feels useful for
    // simple tasks created via QuickAdd's Assign (which doesn't ask for a
    // reviewer).
    try {
      try {
        await tasksApi.sendForReview(item.id);
        toast.success("Sent for review ðŸ“¤");
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        if (typeof detail === "string" && /no reviewer/i.test(detail)) {
          await tasksApi.changeStatus(item.id, { status: "DONE" });
          toast.success("Task completed âœ“");
        } else {
          throw err;
        }
      }
      refreshData();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't complete this task.");
    }
  };

  const handleDelete = async (item: PersonalTodo) => {
    await todoApi.remove(item.id);
    refreshData();
    toast.success("Task deleted");
  };

  const handleEdit = async (item: PersonalTodo, changes: TodoUpdate) => {
    await todoApi.update(item.id, changes);
    refreshData();
  };

  const handleLink = async (item: AssignedItem) => {
    await todoApi.linkTask(item.id);
    refreshData();
    toast.success("Task pinned to today");
  };

  const handleUnlink = async (item: AssignedItem) => {
    if (item.link_id) {
      await todoApi.unlink(item.link_id);
      refreshData();
      toast.success("Task unpinned");
    }
  };

  const handleOpen = (item: PlannerItem) => {
    if (item.source === "assigned") navigate(`/tasks/${item.id}`);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Custom collision: prefer pointerWithin (catches column droppables), fallback to rectIntersection
  const collisionDetection: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    return rectIntersection(args);
  };

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    const item = [...todayItems, ...upcomingItems, ...missedItems].find(t => t.id === id);
    if (item?.source === "personal") setActiveDrag(item);
  };

  const findBucket = (id: string): Bucket | null => {
    if (id === "today" || id === "upcoming" || id === "missed") return id as Bucket;
    if (todayItems.some(t => t.id === id)) return "today";
    if (upcomingItems.some(t => t.id === id)) return "upcoming";
    if (missedItems.some(t => t.id === id)) return "missed";
    return null;
  };

  const bucketSetter = (b: Bucket) =>
    b === "today" ? setTodayItems : b === "upcoming" ? setUpcomingItems : setMissedItems;
  const bucketItems = (b: Bucket): PlannerItem[] =>
    b === "today" ? todayItems : b === "upcoming" ? upcomingItems : missedItems;

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;

    const taskId = String(active.id);
    const overId = String(over.id);
    if (taskId === overId) return;

    const sourceBucket = findBucket(taskId);
    const targetBucket = findBucket(overId);
    if (!sourceBucket || !targetBucket) return;

    // Same column â†’ reorder locally (visual only; backend ordering not persisted yet)
    if (sourceBucket === targetBucket) {
      const items = bucketItems(sourceBucket);
      const oldIdx = items.findIndex(t => t.id === taskId);
      const newIdx = items.findIndex(t => t.id === overId);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
      bucketSetter(sourceBucket)(arrayMove(items, oldIdx, newIdx));
      return;
    }

    // Cross-column â†’ reschedule and refetch
    const targetDate = (() => {
      const today = new Date();
      if (targetBucket === "today") return format(today, "yyyy-MM-dd");
      if (targetBucket === "upcoming") return format(addDays(today, 1), "yyyy-MM-dd");
      return format(addDays(today, -1), "yyyy-MM-dd");
    })();

    // Optimistic move
    const moving = bucketItems(sourceBucket).find(t => t.id === taskId);
    bucketSetter(sourceBucket)(prev => prev.filter(t => t.id !== taskId));
    if (moving) {
      bucketSetter(targetBucket)(prev => [{ ...moving, due_date: targetDate } as PlannerItem, ...prev]);
    }

    try {
      await todoApi.reschedule(taskId, targetDate);
      toast.success(`Task moved to ${targetBucket === "today" ? "Today" : targetBucket === "upcoming" ? "Upcoming" : "Missed"}`);
      refreshData();
    } catch {
      toast.error("Failed to move task");
      refreshData();
    }
  };

  const Column = ({ id, title, icon, items, variant, accent }: {
    id: Bucket;
    title: string;
    icon: React.ReactNode;
    items: PlannerItem[];
    variant: "today" | "upcoming" | "missed";
    accent: string;
  }) => {
    const { setNodeRef, isOver } = useDroppable({ id });
    const personalItems = items.filter(i => i.source === "personal");

    const tileVariant = id === 'today' ? 'is-info' : id === 'upcoming' ? 'is-warm' : 'is-alert';
    return (
      <div className={`gt-column flex flex-col ${isOver ? "is-over" : ""}`}>
        <div className="gt-column-head">
          <div className={`gt-icon-tile ${tileVariant}`}>
            {icon}
          </div>
          <div className="flex flex-col">
            <h2 className="gt-display" style={{ fontSize: 16, color: 'var(--gt-text-primary)', lineHeight: 1, marginTop: 1 }}>{title}</h2>
          </div>
          <span className="gt-count gt-numerals">{String(items.length).padStart(2, '0')}</span>
        </div>

        <div ref={setNodeRef} className="p-3 space-y-2.5 min-h-[600px] max-h-[75vh] overflow-y-auto gt-scroll">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="gt-loading-ring" />
            </div>
          ) : items.length === 0 ? (
            <div className="gt-empty">
              <div className="gt-empty-leaf"><span className="gt-leaf" /></div>
              <div className="gt-empty-title">Nothing here</div>
              <div className="gt-page-sub">A clear column is a <em className="gt-serif-italic">good thing</em>.</div>
            </div>
          ) : (
            <SortableContext items={personalItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
              {items.map(item => (
                <TodoCardComponent
                  key={item.id}
                  item={item}
                  onStatusChange={handleStatusChange}
                  onOpen={handleOpen}
                  onLink={handleLink}
                  onUnlink={handleUnlink}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  variant={variant}
                  sourceTaskStatus={item.source === "personal" ? getSourceTaskStatus(item as PersonalTodo) : null}
                  onStartFromChecklist={handleStartFromChecklist}
                  onPreparorComplete={handlePreparorComplete}
                  onReviewerView={handleReviewerView}
                  onReviewerComplete={handleReviewerComplete}
                  onAssignedStart={handlePreparorAssignedStart}
                  onAssignedComplete={handlePreparorAssignedComplete}
                  entityNames={entityNames}
                />
              ))}
            </SortableContext>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="gt-shell min-h-screen">
      <div className="w-full px-4 md:px-7 pt-2 pb-4 md:py-7 space-y-3 md:space-y-6 gt-fade-in">
        {/* â”€â”€ Row 1: Title + 3 donut rings + Employee Planner button â”€â”€ */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, paddingBottom: 4 }}
        >
          {/* Title */}
          <div style={{ flexShrink: 0 }}>
            <h1 className="gt-page-title" style={{ lineHeight: 1.05 }}>
              Daily <em>Planner</em>
            </h1>
            <p className="gt-page-sub gt-numerals" style={{ marginTop: 4 }}>
              {format(new Date(), "MMMM d, yyyy")}
            </p>
          </div>

          {/* 3 Stat rings â€” one per planner column (Today / Upcoming / Missed).
              Center = how many tasks are in that bucket, arc + caption = how
              many are done. Every colour is a theme variable that flips for
              light / dark / high-contrast, so the number always reads. */}
          {summary && (() => {
            // Per-column stats from the three buckets (unfiltered, so the cards
            // reflect the whole day regardless of the search/filter bar).
            const isDoneItem = (it: PlannerItem) =>
              it.source === 'personal' ? it.status === 'COMPLETED' : it.status === 'DONE';
            const bucketStat = (items: PlannerItem[]) => {
              const count = items.length;
              const done = items.filter(isDoneItem).length;
              return { count, done, pct: count ? Math.round((done / count) * 100) : 0 };
            };
            const tStat = bucketStat(todayItems);
            const uStat = bucketStat(upcomingItems);
            const mStat = bucketStat(missedItems);
            // Ring colour = column identity (blue / amber / red), arc fill = how
            // many of that bucket are done; empties fall back to neutral grey.
            const rings = [
              {
                label: 'Today', value: tStat.count, pct: tStat.pct, sub: `${tStat.done} done`,
                ariaLabel: `Today: ${tStat.count} tasks, ${tStat.done} done`,
                color: tStat.count ? 'var(--gt-status-progress-fg)' : 'var(--gt-text-tertiary)',
                subC:  tStat.count ? 'var(--gt-status-progress-fg)' : 'var(--gt-text-tertiary)',
              },
              {
                label: 'Upcoming', value: uStat.count, pct: uStat.pct, sub: `${uStat.done} done`,
                ariaLabel: `Upcoming: ${uStat.count} tasks, ${uStat.done} done`,
                color: uStat.count ? 'var(--gt-prio-med-fg)' : 'var(--gt-text-tertiary)',
                subC:  uStat.count ? 'var(--gt-prio-med-fg)' : 'var(--gt-text-tertiary)',
              },
              {
                label: 'Missed', value: mStat.count, pct: mStat.pct, sub: `${mStat.done} done`,
                ariaLabel: `Missed: ${mStat.count} tasks, ${mStat.done} done`,
                color: mStat.count ? 'var(--gt-status-overdue-fg)' : 'var(--gt-text-tertiary)',
                subC:  mStat.count ? 'var(--gt-status-overdue-fg)' : 'var(--gt-text-tertiary)',
              },
            ];
            const SZ = 56; const R = 22; const SW = 5;
            const C = 2 * Math.PI * R;
            return (
              <div className="hidden md:flex items-center gap-3" style={{ flex: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                {rings.map((ring) => {
                  const dash = C * (ring.pct / 100);
                  return (
                    <div
                      key={ring.label}
                      role="img"
                      aria-label={ring.ariaLabel}
                      style={{
                        position: 'relative',
                        display: 'flex', alignItems: 'center', gap: 12,
                        background: 'var(--gt-surface-0)',
                        borderRadius: 16,
                        padding: '12px 18px 12px 16px',
                        minWidth: 134,
                        overflow: 'hidden',
                        boxShadow: 'var(--gt-elev-3)',
                      }}
                    >
                      <span aria-hidden style={{
                        position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
                        background: ring.color,
                      }} />
                      <div style={{ position: 'relative', width: SZ, height: SZ, flexShrink: 0 }}>
                        <svg width={SZ} height={SZ} viewBox={`0 0 ${SZ} ${SZ}`} style={{ transform: 'rotate(-90deg)' }}>
                          <circle cx={SZ/2} cy={SZ/2} r={R} fill="none" stroke="var(--gt-surface-3)" strokeWidth={SW} />
                          <circle cx={SZ/2} cy={SZ/2} r={R} fill="none" stroke={ring.color} strokeWidth={SW}
                            strokeLinecap="round"
                            strokeDasharray={`${dash} ${C - dash}`}
                            style={{ transition: 'stroke-dasharray 900ms cubic-bezier(0.4,0,0.2,1)' }}
                          />
                        </svg>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span className="gt-numerals" style={{ fontSize: 20, fontWeight: 800, lineHeight: 1, color: 'var(--gt-text-primary)', letterSpacing: '-0.02em' }}>{ring.value}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 44 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gt-text-tertiary)' }}>
                          {ring.label}
                        </span>
                        <span className="gt-numerals" style={{ fontSize: 11.5, fontWeight: 600, color: ring.subC, lineHeight: 1.2 }}>
                          {ring.sub}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {userRole !== "employee" && userRole !== "intern" && (
            <button
              onClick={() => navigate("/tasks/employee-tasks")}
              className="gt-btn is-primary flex-shrink-0"
              style={{ gap: 7 }}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Employee Planner</span>
              <span className="sm:hidden">Team</span>
            </button>
          )}
        </motion.div>

        {/* Quick Add */}
        <QuickAdd onCreate={handleQuickAdd} />

        {/* Filters â€” desktop: full row (search Â· priority Â· status Â· history).
            Mobile: search bar + Filter icon dropdown + History icon-only. */}
        <div className="flex flex-nowrap items-center gap-2 md:gap-3 md:overflow-x-auto">
          <div className="flex-1 min-w-0 md:min-w-[240px] gt-search-wrap">
            <Search />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasksâ€¦"
              className="gt-input is-search"
            />
          </div>

          {/* Desktop: Priority + Status inline dropdowns */}
          <div className="hidden md:block w-[160px] flex-shrink-0">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Priorities</SelectItem>
                <SelectItem value="URGENT">Urgent</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="hidden md:block w-[160px] flex-shrink-0">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mobile-only: Filter icon â†’ opens a popover containing both
              Priority and Status selects */}
          <div className="md:hidden relative">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(v => !v)}
              className={`gt-planner-filter-icon-btn${mobileFiltersOpen ? ' is-open' : ''}`}
              aria-label="Filters"
              title="Filters"
              aria-haspopup="dialog"
              aria-expanded={mobileFiltersOpen}
            >
              <Filter className="w-4 h-4" />
              {(priorityFilter !== 'ALL' || statusFilter !== 'ALL') && (
                <span className="gt-planner-filter-icon-btn__dot" aria-hidden />
              )}
            </button>
            {mobileFiltersOpen && (
              <>
                <div
                  className="gt-planner-filter-popover__backdrop"
                  onClick={() => setMobileFiltersOpen(false)}
                  aria-hidden
                />
                <div role="dialog" aria-label="Filter tasks" className="gt-planner-filter-popover">
                  <div className="gt-planner-filter-popover__title">Filter tasks</div>
                  <label className="gt-planner-filter-popover__label">Priority</label>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Priorities</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="LOW">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="gt-planner-filter-popover__label">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Status</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      <SelectItem value="COMPLETED">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="gt-planner-filter-popover__actions">
                    <button
                      type="button"
                      onClick={() => { setPriorityFilter('ALL'); setStatusFilter('ALL'); }}
                      className="gt-planner-filter-popover__reset"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => setMobileFiltersOpen(false)}
                      className="gt-planner-filter-popover__done"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* History â€” text + icon on desktop, icon-only on mobile */}
          <button
            onClick={() => navigate("/todo/history")}
            className="gt-btn is-ghost gt-planner-history-btn flex-shrink-0"
            title="View task history"
            aria-label="View task history"
          >
            <History className="w-4 h-4" />
            <span className="hidden md:inline">View Task History</span>
          </button>
        </div>

        {/* Three Columns â€” desktop grid, mobile horizontal swipe carousel */}
        <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="gt-planner-carousel">
            {/* Mobile-only nav: arrows + dot indicators */}
            <div className="gt-planner-carousel__nav" role="tablist" aria-label="Switch column">
              <button
                type="button"
                className="gt-planner-carousel__arrow"
                onClick={() => slideToColumn(activeColumnIdx - 1)}
                disabled={activeColumnIdx === 0}
                aria-label="Previous column"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="gt-planner-carousel__dots">
                <span className="gt-planner-carousel__dot-label">{COLUMN_LABELS[activeColumnIdx]}</span>
                {COLUMN_LABELS.map((label, idx) => (
                  <button
                    key={label}
                    type="button"
                    role="tab"
                    aria-selected={activeColumnIdx === idx}
                    aria-label={`Show ${label}`}
                    className={`gt-planner-carousel__dot${activeColumnIdx === idx ? ' is-active' : ''}`}
                    onClick={() => slideToColumn(idx)}
                  />
                ))}
              </div>
              <button
                type="button"
                className="gt-planner-carousel__arrow"
                onClick={() => slideToColumn(activeColumnIdx + 1)}
                disabled={activeColumnIdx === 2}
                aria-label="Next column"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div
              ref={carouselRef}
              onScroll={onCarouselScroll}
              className="gt-planner-carousel__track"
            >
              <Column id="today" title="Today" icon={<Sun className="w-4 h-4" />} items={filteredToday} variant="today" accent="" />
              <Column id="upcoming" title="Upcoming" icon={<CalendarRange className="w-4 h-4" />} items={filteredUpcoming} variant="upcoming" accent="" />
              <Column id="missed" title="Missed" icon={<AlertTriangle className="w-4 h-4" />} items={filteredMissed} variant="missed" accent="" />
            </div>
          </div>
          {createPortal(
            <DragOverlay dropAnimation={null}>
              {activeDrag ? (
                <div style={{ width: 360, opacity: 0.95, cursor: 'grabbing', boxShadow: '0 18px 38px -8px var(--gt-overlay-slate-28)' }}>
                  <TodoCardComponent
                    item={activeDrag}
                    onStatusChange={handleStatusChange}
                    onOpen={handleOpen}
                    onLink={handleLink}
                    onUnlink={handleUnlink}
                    variant="today"
                    entityNames={entityNames}
                  />
                </div>
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>


      </div>

      {/* Send-for-Review confirm modal â€” fires when preparor completes the LAST checklist item */}
      <AnimatePresence>
        {reviewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'var(--gt-overlay-slate-55)', backdropFilter: 'blur(4px)' }}
            onClick={() => setReviewModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: 'spring', stiffness: 320, damping: 26 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-5 border-b border-gray-100 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-md shrink-0">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900">Send for review?</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    All checklist items for <span className="font-semibold text-gray-700">{reviewModal.taskTitle}</span> are complete. Submit it to the reviewer now?
                  </p>
                </div>
                <button
                  onClick={() => setReviewModal(null)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-6 py-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReviewModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmSendForReview}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-md"
                >
                  <Send className="w-4 h-4" /> Send for Review
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Remaining-checklist block modal â€” fires when other checklist items
          for the same task are still open. */}
      <AnimatePresence>
        {remainingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'var(--gt-overlay-slate-55)', backdropFilter: 'blur(4px)' }}
            onClick={() => setRemainingModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: 'spring', stiffness: 320, damping: 26 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-5 border-b border-gray-100 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900">Item completed â€” more remain</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    This item is done. <span className="font-semibold text-gray-700">{remainingModal.remaining}</span> other checklist item{remainingModal.remaining === 1 ? '' : 's'} of <span className="font-semibold text-gray-700">{remainingModal.taskTitle}</span> {remainingModal.remaining === 1 ? 'is' : 'are'} still open. Finish those first to send the task for review.
                  </p>
                </div>
                <button
                  onClick={() => setRemainingModal(null)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-6 py-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => setRemainingModal(null)}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-amber-500 hover:bg-amber-600 shadow-md"
                >
                  Got it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}